import React, { useEffect, useRef, useState } from 'react';
import './MetricCard.css';

const MetricCard = ({ title, value, subtitle = "", comparisonValue = null, icon = null }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [displayValue, setDisplayValue] = useState(0);
  const cardRef = useRef(null);

  // Extract numeric value for count-up animation
  const getNumericValue = (val) => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      const match = val.match(/[\d,]+\.?\d*/);
      return match ? parseFloat(match[0].replace(/,/g, '')) : 0;
    }
    return 0;
  };

  const numericValue = getNumericValue(value);
  const isStringValue = typeof value === 'string' && !value.match(/^\d+$/);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible && !isStringValue) {
      const duration = 1800; // Slightly faster for better feel
      const startTime = performance.now();
      const startValue = 0;

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Improved easing function for smoother animation
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentValue = startValue + (numericValue - startValue) * easeOutCubic;
        
        setDisplayValue(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setDisplayValue(numericValue);
        }
      };

      // Add a small delay for better visual flow
      setTimeout(() => {
        requestAnimationFrame(animate);
      }, 200);
    } else if (isVisible && isStringValue) {
      setDisplayValue(value);
    }
  }, [isVisible, numericValue, isStringValue, value]);

  const formatDisplayValue = (val) => {
    if (isStringValue) return val;
    // Check if the original value title contains "Hours" or "Time" to add "h" suffix
    if (title && (title.includes('Hours') || title.includes('Time'))) {
      return `${Math.round(val)}h`;
    }
    // Display 0 decimal points for all numeric values
    return Math.round(val);
  };

  const formatComparisonValue = (val) => {
    if (!val) return null;
    if (typeof val === 'string') return val;
    if (typeof val === 'number') {
      // Check if the original value title contains "Hours" or "Time" to add "h" suffix
      if (title && (title.includes('Hours') || title.includes('Time'))) {
        return `${Math.round(val)}h`;
      }
      return Math.round(val);
    }
    return val;
  };

  // Determine if comparison value is higher or lower for color coding
  const getComparisonColor = () => {
    if (!comparisonValue || !value) return 'red'; // Default to red if no comparison
    
    const mainNumeric = getNumericValue(value);
    const comparisonNumeric = getNumericValue(comparisonValue);
    
    if (comparisonNumeric > mainNumeric) {
      return 'green'; // Comparison value is higher
    } else if (comparisonNumeric < mainNumeric) {
      return 'red'; // Comparison value is lower
    } else {
      return 'neutral'; // Values are equal
    }
  };

  const comparisonColor = getComparisonColor();

  return (
    <div 
      ref={cardRef}
      className={`metric-card ${isVisible ? 'visible' : ''}`}
    >
      <div className={`metric-card-icon ${!icon ? 'metric-card-icon-empty' : ''}`}>
        {icon || null}
      </div>
      <div className="metric-title">{title}</div>
      <div className="metric-value">
        {formatDisplayValue(displayValue)}
      </div>
      {subtitle && <div className="metric-subtitle">{subtitle}</div>}
      {comparisonValue && (
        <div className={`metric-comparison comparison-${comparisonColor}`}>
          <span className="comparison-label">vs</span>
          {formatComparisonValue(comparisonValue)}
        </div>
      )}
    </div>
  );
};

export default MetricCard; 